/**
 * Small pure helpers for the Spend Landscape charts and tables.
 */

/**
 * Spend Landscape chart colours, from the Mithra 3.0 design: a charcoal-to-grey ramp for magnitude
 * (src/style/colors.scss black and grey tokens). Only the map bubbles are blue.
 */
export const SPEND_BAR_COLOR = '#2F3033';
/** Treemap: light to dark by spend */
export const SPEND_RAMP: [string, string] = ['#A3A6AD', '#1B1B1F'];
/** Bars after the first 80% of spend */
export const SPEND_TAIL = '#C2C6CC';

/** Smallest, middle and largest value, for a bubble size legend. Fewer when values repeat. */
export function sizeLegendValues(values: number[]): number[] {
    const sorted = values.filter(v => Number.isFinite(v) && v > 0).sort((a, b) => a - b);
    if (sorted.length === 0) return [];
    const picks = [sorted[0], sorted[Math.floor((sorted.length - 1) / 2)], sorted[sorted.length - 1]];
    return Array.from(new Set(picks));
}

export type RankLevel = 'high' | 'medium' | 'low';

/** Opportunity rank runs from 0 (low) to 4 (high); around 2 is typical */
export const RANK_HIGH_FROM = 2.05;
export const RANK_MEDIUM_FROM = 1.95;
export const RANK_MAX = 4;

export function rankLevel(rank: number): RankLevel {
    if (rank >= RANK_HIGH_FROM) return 'high';
    if (rank >= RANK_MEDIUM_FROM) return 'medium';
    return 'low';
}

/** Width (0 to 1) of a value's bar relative to the largest value shown */
export function barFraction(value: number, max: number): number {
    if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0 || value <= 0) return 0;
    return Math.min(1, value / max);
}

/** The share of the "Other" slice, or null when there is none */
export function otherShare(rows: {s_id: string | null, spend: number}[]): number | null {
    const total = rows.reduce((sum, r) => sum + (r.spend > 0 ? r.spend : 0), 0);
    const other = rows.find(r => r.s_id === null);
    if (!other || !other.spend || other.spend <= 0 || total <= 0) return null;
    return other.spend / total;
}
